/**
 * SourceSelector Component
 * Allows users to select where to search for SmartSlot matches
 */

import { useState, useEffect } from 'react';
import { ChevronDown, Loader2, Search } from 'lucide-react';
import { playlistService } from '../../api/services/playlists';
import type { PlaylistSummary } from '../../api/types';
import type { MatchSource } from '../../api/services/smart-slots';
import { useUIStore } from '../../stores/ui';

// Re-export MatchSource for convenience
export type { MatchSource };

export interface SourceSelectorProps {
  source: MatchSource;
  onSourceChange: (source: MatchSource) => void;
  isSearching?: boolean;
}

/**
 * Get display text for current source
 */
function getSourceDisplayText(source: MatchSource, playlistCount?: number): string {
  switch (source.type) {
    case 'workbench':
      return 'Current playlist only';
    case 'playlists':
      return playlistCount
        ? `${playlistCount} selected playlist${playlistCount === 1 ? '' : 's'}`
        : 'Selected playlists';
    case 'playlist':
      return 'Single playlist';
    case 'liked':
      return 'Liked Songs';
    case 'all':
      return 'All playlists';
  }
}

export function SourceSelector({ source, onSourceChange, isSearching = false }: SourceSelectorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Use persisted playlist selection from UI store
  const { selectedPlaylistIds, setSelectedPlaylistIds } = useUIStore();

  // Fetch playlists on first expand
  useEffect(() => {
    if (isExpanded && playlists.length === 0 && !isLoading && !error) {
      const fetchPlaylists = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await playlistService.list({ limit: 50 });
          setPlaylists(response.playlists);
        } catch (err) {
          console.error('Failed to fetch playlists:', err);
          setError('Failed to load playlists');
        } finally {
          setIsLoading(false);
        }
      };
      fetchPlaylists();
    }
  }, [isExpanded, playlists.length, isLoading, error]);

  // Filter playlists based on search query
  const filteredPlaylists = playlists.filter((playlist) =>
    playlist.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle source type change
  const handleSourceTypeChange = (type: MatchSource['type']) => {
    if (type === 'workbench') {
      onSourceChange({ type: 'workbench' });
    } else if (type === 'playlists') {
      onSourceChange({ type: 'playlists', playlistIds: selectedPlaylistIds });
    } else if (type === 'liked') {
      onSourceChange({ type: 'liked' });
    }
  };

  // Handle playlist checkbox toggle
  const handlePlaylistToggle = (playlistId: string) => {
    const newIds = selectedPlaylistIds.includes(playlistId)
      ? selectedPlaylistIds.filter((id) => id !== playlistId)
      : [...selectedPlaylistIds, playlistId];

    setSelectedPlaylistIds(newIds);

    // Update source immediately if playlists mode is active
    if (source.type === 'playlists') {
      onSourceChange({ type: 'playlists', playlistIds: newIds });
    }
  };

  // Toggle expanded state
  const handleToggleExpand = () => {
    if (!isSearching) {
      setIsExpanded(!isExpanded);
    }
  };

  const playlistCount = source.type === 'playlists' ? source.playlistIds.length : undefined;
  const displayText = getSourceDisplayText(source, playlistCount);

  return (
    <div className="border border-zinc-800 rounded-lg">
      {/* Collapsed Header Button */}
      <button
        onClick={handleToggleExpand}
        disabled={isSearching}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm text-zinc-300 font-medium">Search in:</span>
          <span className="text-sm text-emerald-400">{displayText}</span>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-zinc-500 transition-transform ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expanded Options */}
      {isExpanded && (
        <div className="border-t border-zinc-800 p-3 space-y-3">
          {/* Radio Option: Current Playlist */}
          <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-900/50 cursor-pointer transition-colors">
            <input
              type="radio"
              name="source"
              checked={source.type === 'workbench'}
              onChange={() => handleSourceTypeChange('workbench')}
              className="w-4 h-4 accent-emerald-500"
            />
            <div className="flex-1">
              <div className="text-sm text-zinc-300 font-medium">Current playlist only</div>
              <div className="text-xs text-zinc-500">Fast search in current workbench</div>
            </div>
          </label>

          {/* Radio Option: Selected Playlists */}
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-900/50 cursor-pointer transition-colors">
              <input
                type="radio"
                name="source"
                checked={source.type === 'playlists'}
                onChange={() => handleSourceTypeChange('playlists')}
                className="w-4 h-4 accent-emerald-500"
              />
              <div className="flex-1">
                <div className="text-sm text-zinc-300 font-medium">Selected playlists</div>
                <div className="text-xs text-zinc-500">Choose specific playlists to search</div>
              </div>
            </label>

            {/* Playlist Multi-Select (shown when playlists mode is active) */}
            {source.type === 'playlists' && (
              <div className="ml-7 border border-zinc-800 rounded-lg p-3 bg-zinc-900/30 space-y-2">
                {/* Search Input */}
                <div className="relative">
                  <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-zinc-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search playlists..."
                    className="w-full pl-8 pr-3 py-1.5 text-xs bg-zinc-800/50 border border-zinc-700 rounded-md text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/50"
                  />
                </div>

                {/* Playlist List */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                    <span className="ml-2 text-sm text-zinc-500">Loading playlists...</span>
                  </div>
                ) : error ? (
                  <div className="py-4 text-center">
                    <p className="text-xs text-red-400">{error}</p>
                  </div>
                ) : filteredPlaylists.length === 0 ? (
                  <div className="py-4 text-center">
                    <p className="text-xs text-zinc-500">
                      {searchQuery ? 'No matching playlists' : 'No playlists found'}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-80 overflow-y-auto space-y-1 pr-1">
                    {/* Selection Summary */}
                    {selectedPlaylistIds.length > 0 && (
                      <div className="text-[10px] text-emerald-400 mb-1 px-2">
                        {selectedPlaylistIds.length} playlist{selectedPlaylistIds.length === 1 ? '' : 's'} selected
                      </div>
                    )}

                    {filteredPlaylists.map((playlist) => (
                      <label
                        key={playlist.id}
                        className="flex items-center gap-2 p-2 rounded hover:bg-zinc-800/50 cursor-pointer transition-colors"
                      >
                        <div className="relative flex items-center justify-center w-4 h-4">
                          <input
                            type="checkbox"
                            checked={selectedPlaylistIds.includes(playlist.id)}
                            onChange={() => handlePlaylistToggle(playlist.id)}
                            className="w-4 h-4 accent-emerald-500"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-zinc-300 truncate font-medium">
                            {playlist.name}
                          </div>
                          <div className="text-[10px] text-zinc-600">
                            {playlist.tracksCount} track{playlist.tracksCount === 1 ? '' : 's'}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Radio Option: Liked Songs (disabled/coming soon) */}
          <label className="flex items-center gap-3 p-2 rounded-lg opacity-50 cursor-not-allowed">
            <input
              type="radio"
              name="source"
              disabled
              className="w-4 h-4 accent-emerald-500"
            />
            <div className="flex-1">
              <div className="text-sm text-zinc-300 font-medium flex items-center gap-2">
                Liked Songs
                <span className="text-[10px] text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded uppercase tracking-wider">
                  Coming Soon
                </span>
              </div>
              <div className="text-xs text-zinc-500">Search your Liked Songs collection</div>
            </div>
          </label>
        </div>
      )}
    </div>
  );
}
